import type {
  EmbeddingStatus,
  EmbeddingStatusResponse,
  ProjectFile,
} from "@/services/project-service"

export function getEffectiveProjectFileStatus(
  file: ProjectFile,
  embeddingStatus?: EmbeddingStatusResponse
): EmbeddingStatus {
  // The Responses vector store is the active retrieval path. Its exact,
  // backend-validated membership is authoritative over the parallel legacy
  // Azure AI Search status. Analysis-only files are terminal as long as their
  // canonical blob still exists.
  if (
    file.needs_reupload !== true &&
    (file.vector_store_eligible === false || file.in_vector_store === true)
  ) {
    return "completed"
  }

  return (
    embeddingStatus?.files.find((candidate) => candidate.filename === file.filename)?.status ??
    file.embedding_status ??
    "completed"
  )
}

export function isProjectFileEffectivelyPending(
  file: ProjectFile,
  embeddingStatus?: EmbeddingStatusResponse
): boolean {
  if (file.vector_store_eligible === false || file.needs_reupload === true) return false

  const status = getEffectiveProjectFileStatus(file, embeddingStatus)
  return status === "pending" || status === "processing"
}

export type ProjectFileStatusSummary = {
  allFilesAreAnalysisReady: boolean
  fileCount: number
  indexedFileCount: number
  pendingFileCount: number
  retrievalEligibleFileCount: number
}

export type ProjectFileGeneration = Pick<ProjectFile, "filename" | "uploaded_at">

export function mergeProjectFileGenerations(
  current: readonly ProjectFileGeneration[],
  uploaded: readonly ProjectFileGeneration[]
): ProjectFileGeneration[] {
  const generations = new Map(current.map((file) => [file.filename, file]))
  for (const file of uploaded) {
    const existing = generations.get(file.filename)
    if (!existing || generationIsCurrentOrNewer(file, existing)) {
      generations.set(file.filename, file)
    }
  }
  return [...generations.values()]
}

export function hasUnobservedProjectFileGenerations(
  files: readonly ProjectFile[] | undefined,
  expected: readonly ProjectFileGeneration[]
): boolean {
  return expected.some(
    (generation) =>
      !files?.some(
        (file) =>
          file.filename === generation.filename && generationIsCurrentOrNewer(file, generation)
      )
  )
}

function generationIsCurrentOrNewer(
  observed: ProjectFileGeneration,
  expected: ProjectFileGeneration
): boolean {
  if (observed.uploaded_at === expected.uploaded_at) return true
  const observedAt = Date.parse(observed.uploaded_at)
  const expectedAt = Date.parse(expected.uploaded_at)
  return Number.isFinite(observedAt) && Number.isFinite(expectedAt) && observedAt >= expectedAt
}

export function getProjectFileStatusSummary(
  files: readonly ProjectFile[] | undefined,
  embeddingStatus?: EmbeddingStatusResponse
): ProjectFileStatusSummary {
  const projectFiles = files ?? []
  const retrievalEligibleFiles = projectFiles.filter((file) => file.vector_store_eligible !== false)

  const indexedFileCount = retrievalEligibleFiles.filter((file) => {
    if (file.needs_reupload === true || file.in_vector_store === false) return false
    return (
      file.in_vector_store === true ||
      getEffectiveProjectFileStatus(file, embeddingStatus) === "completed"
    )
  }).length

  return {
    allFilesAreAnalysisReady:
      projectFiles.length > 0 &&
      retrievalEligibleFiles.length === 0 &&
      projectFiles.every((file) => file.needs_reupload !== true),
    fileCount: projectFiles.length,
    indexedFileCount,
    pendingFileCount: retrievalEligibleFiles.filter((file) =>
      isProjectFileEffectivelyPending(file, embeddingStatus)
    ).length,
    retrievalEligibleFileCount: retrievalEligibleFiles.length,
  }
}
